import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Table,
  Tag,
  Typography,
  Space,
  Popconfirm,
  AutoComplete,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getCustomers } from "../../RestServices/authService";

const { Title } = Typography;
const { TextArea } = Input;

const SubscriptionModal = ({
  visible,
  onCancel,
  onSubmit,
  customer,
  subscriptions = [],
  loading,
  onDelete,
  isActive,
  form,
}) => {
  const { token } = useSelector((state) => state.Authentication.user);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // Mirror the picked customer's display label so the AutoComplete shows
  // "Mubashar (+923167004282)" instead of the raw ObjectId after selection.
  // Stored separately because AntD's AutoComplete writes its `value` (the
  // _id) into the input, which is what gets posted to the backend.
  const [pickedCustomerLabel, setPickedCustomerLabel] = useState("");

  useEffect(() => {
    if (visible && customer) {
      form.setFieldsValue({
        customerId: customer._id,
      });
    } else if (visible && !customer) {
      form.setFieldsValue({
        startDate: moment(),
        endDate: moment().add(1, "month"),
      });
    }
    if (editingSubscription) {
      form.setFieldsValue({
        subscriptionId: editingSubscription._id,
        customerId: editingSubscription.customerId,
        startDate: moment(editingSubscription.startDate),
        endDate: moment(editingSubscription.endDate),
        paidAmount: editingSubscription.paidAmount || 0,
        isActive: editingSubscription.isActive !== false,
        notes: editingSubscription.notes,
      });
    }
  }, [visible, customer, editingSubscription, form]);

  // Debounce the customer autocomplete: only hit the backend 400ms after
  // the admin stops typing, and drop out-of-order responses.
  const searchTimerRef = useRef(null);
  const searchSeqRef = useRef(0);

  useEffect(() => {
    // Clean up any pending debounce timer on unmount/close.
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const handleSearchCustomer = (searchText) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!searchText || searchText.length < 2) {
      setCustomerSearchOptions([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      const seq = ++searchSeqRef.current;
      try {
        const data = await getCustomers(token, {
          page: 1,
          limit: 10,
          search: searchText,
        });
        if (seq !== searchSeqRef.current) return; // stale response

        const options = data.customers.docs.map((cust) => ({
          value: cust._id,
          label: `${cust.name || "N/A"} (${cust.phoneNumber || "N/A"})`,
          customer: cust,
        }));

        setCustomerSearchOptions(options);
      } catch (err) {
        console.error("Failed to search customers:", err);
      } finally {
        if (seq === searchSeqRef.current) setSearchLoading(false);
      }
    }, 400);
  };

  const handleEdit = (subscription) => {
    setEditingSubscription(subscription);
    form.setFieldsValue({
      subscriptionId: subscription._id,
      customerId: subscription.customerId,
      startDate: moment(subscription.startDate),
      endDate: moment(subscription.endDate),
      paidAmount: subscription.paidAmount || 0,
      isActive: subscription.isActive !== false,
      notes: subscription.notes || "",
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setEditingSubscription(null);
    setPickedCustomerLabel("");
    onCancel();
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      onSubmit(values);
      setEditingSubscription(null);
      setPickedCustomerLabel("");
    });
  };

  // When admin picks a customer from the AutoComplete dropdown, store both
  // the _id (in the form, posted to backend) and a friendly label (shown
  // in the input). Without this, AntD's AutoComplete echoes the raw _id —
  // looks broken even though submission works.
  const handleCustomerSelect = (value, option) => {
    form.setFieldsValue({ customerId: value });
    setPickedCustomerLabel(option?.label || value);
  };

  const subscriptionColumns = [
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => moment(date).format("YYYY-MM-DD"),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (date) => moment(date).format("YYYY-MM-DD"),
    },
    {
      title: "Status",
      key: "status",
      render: (record) => {
        if (record.isActive === false) {
          return <Tag color="red">Disabled</Tag>;
        }
        if (isActive(record)) {
          return <Tag color="green">Active</Tag>;
        }
        if (moment(record.startDate).isAfter(moment())) {
          return <Tag color="orange">Scheduled</Tag>;
        }
        return <Tag>Expired</Tag>;
      },
    },
    {
      title: "Paid",
      dataIndex: "paidAmount",
      key: "paidAmount",
      render: (amount) => `Rs ${Number(amount || 0).toLocaleString()}`,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => moment(date).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this subscription?"
            onConfirm={() => onDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ marginBottom: 0 }}>
            {customer
              ? `Subscriptions for ${customer.name}`
              : editingSubscription
              ? "Edit Subscription"
              : "Create New Subscription"}
          </Title>
        </div>
      }
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
    >
      {customer && subscriptions.length > 0 && (
        <div style={{ marginBottom: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <Title level={5}>Existing Subscriptions</Title>
            <Tag color="gold" style={{ fontWeight: 600 }}>
              Total Paid: Rs{" "}
              {subscriptions
                .reduce((sum, sub) => sum + (Number(sub.paidAmount) || 0), 0)
                .toLocaleString()}
            </Tag>
          </div>
          <Table
            columns={subscriptionColumns}
            dataSource={subscriptions}
            loading={loading}
            pagination={false}
            rowKey="_id"
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          startDate: moment(),
          endDate: moment().add(1, "month"),
        }}
      >
        <Form.Item name="subscriptionId" hidden>
          <Input />
        </Form.Item>

        {!customer && (
          <Form.Item
            name="customerId"
            label="Customer"
            rules={[{ required: true, message: "Please select a customer" }]}
          >
            <AutoComplete
              options={customerSearchOptions}
              onSearch={handleSearchCustomer}
              onSelect={handleCustomerSelect}
              loading={searchLoading}
              placeholder="Search customer by name or phone number"
              // Show "Name (+phone)" after selection instead of the raw
              // ObjectId. The backing form value stays as the _id — only
              // the visible input text is the friendly label.
              value={pickedCustomerLabel || undefined}
              onChange={(v) => {
                // User typing — clear the prior pick so they can start fresh.
                setPickedCustomerLabel(v);
                form.setFieldsValue({ customerId: undefined });
              }}
              filterOption={false}
              style={{ width: "100%" }}
            />
          </Form.Item>
        )}

        <Form.Item
          name="startDate"
          label="Start Date"
          rules={[{ required: true, message: "Please select start date" }]}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="endDate"
          label="End Date"
          rules={[{ required: true, message: "Please select end date" }]}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="paidAmount"
          label="Paid Amount (Rs)"
          rules={[
            {
              type: "number",
              min: 0,
              message: "Paid amount cannot be negative",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            step={100}
            placeholder="Amount the customer paid for this subscription"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => `${value}`.replace(/,/g, "")}
          />
        </Form.Item>

        {editingSubscription && (
          <Form.Item
            name="isActive"
            label="Status"
            tooltip="Inactive immediately revokes the customer's AI intelligence access"
          >
            <Select style={{ width: "100%" }}>
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="notes"
          label="Notes"
          rules={[{ max: 500, message: "Notes cannot exceed 500 characters" }]}
        >
          <TextArea rows={3} placeholder="Optional notes about this subscription" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              {editingSubscription ? "Update Subscription" : "Create Subscription"}
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
            {editingSubscription && (
              <Button onClick={() => setEditingSubscription(null)}>
                Cancel Edit
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SubscriptionModal;

